<script setup lang="ts">
import { useI18n } from '@/i18n';
import type { Subscription } from '@/types/billing';
import { Badge } from '@dabble/quillui';
import { formatDistanceToNow } from 'date-fns';
import { computed } from 'vue';

const props = defineProps<{ subscription?: Subscription | null }>();

// Composables
const { t } = useI18n();

// Resolve the pause-resume timestamp (Stripe seconds). Prefer Stripe's native
// pause_collection.resumes_at; fall back to the legacy homebrew marker on
// metadata.is_paused_until (or trial_end, which the old system reused).
function resolvePauseResumeSeconds(sub: Subscription): number | null {
  const native = sub.pause_collection?.resumes_at;
  if (typeof native === 'number') return native;
  const legacyMeta = sub.metadata?.is_paused_until;
  if (typeof legacyMeta === 'number') return legacyMeta;
  if (typeof legacyMeta === 'string' && legacyMeta) {
    const parsed = Number(legacyMeta);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return sub.trial_end ?? null;
}

function isSubscriptionPaused(sub: Subscription): boolean {
  if (sub.status === 'paused') return true;
  if (sub.pause_collection?.behavior) return true;
  if (sub.metadata?.is_paused_until) return true;
  return false;
}

type BadgeVariant = 'neutral' | 'warning' | 'danger' | 'success' | 'info';

const badgeInfo = computed<{ text: string; variant: BadgeVariant; icon: string | null }>(() => {
  const sub = props.subscription;
  let text = '';
  let variant: BadgeVariant = 'neutral';
  let icon: string | null = null;

  if (!sub || sub.status === 'canceled' || sub.status === 'incomplete_expired' || sub.status === 'unpaid') {
    text = t('billing_plan_no_subscription', 'Inactive');
    variant = 'neutral';
  } else if (isSubscriptionPaused(sub)) {
    // A pending cancellation outranks the pause. Paused is checked before cancel_at_period_end
    // in this chain (it has to stay ahead of `trialing`, since a legacy pause reads as trialing),
    // so without this a paused user who cancels sees the badge they already had — no confirmation
    // that anything happened — and "resumes in 5 days" actively misleads someone on their way out.
    // That is what had DAB-922's reporter cancelling three times over.
    if (sub.cancel_at_period_end) {
      text = t('billing_plan_cancels');
      variant = 'warning';
      icon = 'clock';
    } else {
      const resumeSeconds = resolvePauseResumeSeconds(sub);
      const dateStr = resumeSeconds
        ? formatDistanceToNow(new Date(resumeSeconds * 1000), { addSuffix: true })
        : 'an unknown date';
      text = t('billing_plan_paused_resumes', { date: dateStr });
      variant = 'warning';
    }
  } else if (sub.status === 'trialing') {
    const dateStr = sub.trial_end
      ? formatDistanceToNow(new Date(sub.trial_end * 1000), { addSuffix: true })
      : 'an unknown date';
    // The plan name is right next to the badge on the plan card — just the countdown.
    text = t('billing_plan_trial_ends', { date: dateStr });
    variant = 'warning';
    icon = 'clock';
  } else if (sub.cancel_at_period_end) {
    text = t('billing_plan_cancels');
    variant = 'warning';
    icon = 'clock';
  } else if (sub.status === 'past_due') {
    text = t('billing_plan_past_due', 'Past Due');
    variant = 'danger';
  } else if (sub.status === 'incomplete') {
    text = t('billing_plan_incomplete', 'Incomplete');
    variant = 'warning';
  }

  return { text, variant, icon };
});
</script>

<template>
  <Badge v-if="badgeInfo.text" :variant="badgeInfo.variant" :icon-left="badgeInfo.icon ?? undefined">
    {{ badgeInfo.text }}
  </Badge>
</template>
